import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  createVaultWithNip44,
  createVaultWithPassphrase,
  isVaultUnlocked,
  lockVault,
  type Nip44Signer,
  setSealEnabled,
  setVaultConfigured,
  unlockVaultWithNip44,
  unlockVaultWithPassphrase,
  type VaultMeta,
} from '@/lib/crypto/vault';
import { resealProviderSecrets } from '@/lib/crypto/secrets';
import * as storage from '@/lib/workspace/storage';
import type { WorkspaceSettings } from '@/lib/workspace/types';

export type VaultStatus =
  | 'loading'
  | 'no-user'
  | 'no-vault'
  | 'locked'
  | 'unlocking'
  | 'unlocked'
  | 'error';

interface VaultContextValue {
  status: VaultStatus;
  error: string | null;
  hasVault: boolean;
  supportsNip44: boolean;
  unlocked: boolean;
  /** Create vault via NIP-44 (preferred) */
  enableWithNip44: () => Promise<void>;
  /** Create vault with passphrase wrap (fallback) */
  enableWithPassphrase: (passphrase: string) => Promise<void>;
  unlockWithNip44: () => Promise<void>;
  unlockWithPassphrase: (passphrase: string) => Promise<void>;
  lock: () => void;
  /** Mark user activity (resets idle lock timer). */
  touchActivity: () => void;
  /** Disable encryption and leave data as currently decrypted in memory — user should export first */
  disableEncryption: () => Promise<void>;
  clearError: () => void;
}

const VaultContext = createContext<VaultContextValue | null>(null);

function signerAsNip44(user: { signer: unknown } | undefined): Nip44Signer | null {
  if (!user?.signer) return null;
  const s = user.signer as Nip44Signer;
  return s;
}

export function VaultProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const [status, setStatus] = useState<VaultStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [vaultMeta, setVaultMeta] = useState<VaultMeta | undefined>();
  const [idleLockMinutes, setIdleLockMinutes] = useState(30);
  const lastActivityRef = useRef(Date.now());

  const supportsNip44 = Boolean(
    signerAsNip44(user)?.nip44?.encrypt && signerAsNip44(user)?.nip44?.decrypt,
  );

  const touchActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const refreshMeta = useCallback(async () => {
    if (!user) {
      lockVault();
      setVaultMeta(undefined);
      setStatus('no-user');
      return;
    }
    try {
      const settings = await storage.getSettings();
      setVaultMeta(settings.vault);
      setVaultConfigured(Boolean(settings.vault));
      setIdleLockMinutes(
        typeof settings.idleLockMinutes === 'number' ? settings.idleLockMinutes : 30,
      );
      // Honor encryptLocal policy for seal writes
      setSealEnabled(Boolean(settings.vault) && (settings.encryptLocal ?? true));
      if (!settings.vault) {
        lockVault();
        setVaultConfigured(false);
        setSealEnabled(false);
        setStatus('no-vault');
        return;
      }
      // Identity bind: vault.pubkey must match login when set
      if (
        settings.vault.pubkey &&
        settings.vault.pubkey.toLowerCase() !== user.pubkey.toLowerCase()
      ) {
        lockVault();
        setStatus('locked');
        setError(
          'This vault belongs to a different Nostr account. Switch accounts or clear local data.',
        );
        return;
      }
      if (isVaultUnlocked()) {
        setStatus('unlocked');
        touchActivity();
        return;
      }
      // Auto-unlock with NIP-44 when possible
      const signer = signerAsNip44(user);
      if (settings.vault.nip44WrappedKey && signer?.nip44?.decrypt) {
        setStatus('unlocking');
        try {
          await unlockVaultWithNip44(settings.vault, signer, user.pubkey);
          setSealEnabled(settings.encryptLocal ?? true);
          await resealProviderSecrets();
          setStatus('unlocked');
          touchActivity();
          return;
        } catch (err) {
          console.warn('Auto vault unlock failed', err);
          setStatus('locked');
          return;
        }
      }
      setStatus('locked');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Vault error');
      setStatus('error');
    }
  }, [user, touchActivity]);

  useEffect(() => {
    void refreshMeta();
  }, [refreshMeta]);

  // Lock on logout
  useEffect(() => {
    if (!user) {
      lockVault();
      setStatus('no-user');
    }
  }, [user]);

  // Idle lock — vault protects unattended unlocked sessions
  useEffect(() => {
    if (status !== 'unlocked' || !vaultMeta) return;
    const minutes = idleLockMinutes ?? 0;
    if (minutes <= 0) return;

    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };
    const events: (keyof WindowEventMap)[] = [
      'pointerdown',
      'keydown',
      'scroll',
      'touchstart',
    ];
    for (const ev of events) {
      window.addEventListener(ev, onActivity, { passive: true });
    }

    const tick = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= minutes * 60_000 && isVaultUnlocked()) {
        lockVault();
        setStatus('locked');
      }
    }, 15_000);

    return () => {
      for (const ev of events) {
        window.removeEventListener(ev, onActivity);
      }
      window.clearInterval(tick);
    };
  }, [status, vaultMeta, idleLockMinutes]);

  const persistVault = useCallback(async (meta: VaultMeta | undefined, encryptLocal = true) => {
    const settings = await storage.getSettings();
    const next: WorkspaceSettings = {
      ...settings,
      vault: meta,
      encryptLocal: meta ? encryptLocal : false,
      // Bind workspace to this identity when enabling vault
      ownerPubkey: meta?.pubkey ?? settings.ownerPubkey ?? user?.pubkey,
    };
    await storage.saveSettings(next);
    setVaultMeta(meta);
    setVaultConfigured(Boolean(meta));
  }, [user?.pubkey]);

  const enableWithNip44 = useCallback(async () => {
    if (!user) throw new Error('Log in first');
    const signer = signerAsNip44(user);
    if (!signer?.nip44?.encrypt) {
      throw new Error('Your signer does not support NIP-44. Use a passphrase vault instead.');
    }
    setError(null);
    setStatus('unlocking');
    try {
      setSealEnabled(true);
      const meta = await createVaultWithNip44(signer, user.pubkey);
      await persistVault(meta, true);
      await storage.reencryptAllLocalData();
      await resealProviderSecrets();
      setStatus('unlocked');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to enable vault';
      setError(msg);
      setStatus('no-vault');
      throw err;
    }
  }, [user, persistVault]);

  const enableWithPassphrase = useCallback(
    async (passphrase: string) => {
      if (!user) throw new Error('Log in first');
      if (passphrase.length < 12) {
        throw new Error('Passphrase must be at least 12 characters for a strong vault');
      }
      setError(null);
      setStatus('unlocking');
      try {
        setSealEnabled(true);
        const meta = await createVaultWithPassphrase(passphrase);
        meta.pubkey = user.pubkey;
        await persistVault(meta, true);
        await storage.reencryptAllLocalData();
        await resealProviderSecrets();
        setStatus('unlocked');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to enable vault';
        setError(msg);
        setStatus('no-vault');
        throw err;
      }
    },
    [user, persistVault],
  );

  const unlockWithNip44 = useCallback(async () => {
    if (!user || !vaultMeta) throw new Error('No vault');
    const signer = signerAsNip44(user);
    if (!signer) throw new Error('No signer');
    setError(null);
    setStatus('unlocking');
    try {
      await unlockVaultWithNip44(vaultMeta, signer, user.pubkey);
      const settings = await storage.getSettings();
      setSealEnabled(settings.encryptLocal ?? true);
      await resealProviderSecrets();
      setStatus('unlocked');
      touchActivity();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unlock failed';
      setError(msg);
      setStatus('locked');
      throw err;
    }
  }, [user, vaultMeta, touchActivity]);

  const unlockWithPassphrase = useCallback(
    async (passphrase: string) => {
      if (!vaultMeta) throw new Error('No vault');
      setError(null);
      setStatus('unlocking');
      try {
        await unlockVaultWithPassphrase(vaultMeta, passphrase, user?.pubkey);
        const settings = await storage.getSettings();
        setSealEnabled(settings.encryptLocal ?? true);
        await resealProviderSecrets();
        setStatus('unlocked');
        touchActivity();
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Wrong passphrase or corrupt vault';
        setError(msg);
        setStatus('locked');
        throw err;
      }
    },
    [vaultMeta, touchActivity, user?.pubkey],
  );

  const lock = useCallback(() => {
    lockVault();
    setStatus(vaultMeta ? 'locked' : user ? 'no-vault' : 'no-user');
  }, [vaultMeta, user]);

  const disableEncryption = useCallback(async () => {
    if (!isVaultUnlocked() && vaultMeta) {
      throw new Error('Unlock the vault before disabling encryption');
    }
    // Open everything, then write plaintext (seal disabled) while still unlocked
    const projects = await storage.getAllProjects();
    const chats = await storage.getAllChats();
    const messages = await storage.getAllMessages();
    const settings = await storage.getSettings();
    setSealEnabled(false);
    setVaultConfigured(false);
    for (const p of projects) await storage.putProject(p);
    for (const c of chats) await storage.putChat(c);
    for (const m of messages) await storage.putMessage(m);
    await storage.saveSettings({ ...settings, vault: undefined, encryptLocal: false });
    // Unseal API keys to plain storage before locking (load needs session key)
    await resealProviderSecrets();
    lockVault();
    await persistVault(undefined, false);
    setStatus(user ? 'no-vault' : 'no-user');
  }, [vaultMeta, persistVault, user]);

  const value = useMemo<VaultContextValue>(
    () => ({
      status,
      error,
      hasVault: Boolean(vaultMeta),
      supportsNip44,
      unlocked: status === 'unlocked',
      enableWithNip44,
      enableWithPassphrase,
      unlockWithNip44,
      unlockWithPassphrase,
      lock,
      touchActivity,
      disableEncryption,
      clearError: () => setError(null),
    }),
    [
      status,
      error,
      vaultMeta,
      supportsNip44,
      enableWithNip44,
      enableWithPassphrase,
      unlockWithNip44,
      unlockWithPassphrase,
      lock,
      touchActivity,
      disableEncryption,
    ],
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}

export function useVault(): VaultContextValue {
  const ctx = useContext(VaultContext);
  if (!ctx) throw new Error('useVault must be used within VaultProvider');
  return ctx;
}
